import React, { FC, useState, useEffect } from 'react'
import classNames from 'classnames'
import { Box, Grid, makeStyles, Paper, Theme } from '@material-ui/core'
import { useLocation } from 'react-router-dom'
import { Header, Footer, Modal } from 'components'
import { useHistory } from 'react-router-dom'
import {
  SignUp,
  ConfirmSignUp,
  ResendCode,
  Registration,
  SignIn,
  ForgotPassword,
  HaveAQuestion,
  Survey,
  ThankYou,
  AuthRoot
} from './index'
import { AuthFlowSteps, EventStages, IUser } from 'types'

interface IAuthWrapper {
  backgroundImage?: string
  user?: IUser | null
  setUser: (user: IUser) => void
  eventStage?: EventStages
}
export const AuthWrapper: FC<IAuthWrapper> = props => {
  const history = useHistory()
  const queryParams = new URLSearchParams(useLocation().search)
  const classes = useStyles(props)

  const [authState, setAuthState] = useState<AuthFlowSteps>(AuthFlowSteps.Root)
  const [userEmail, setUserEmail] = useState<string>('')

  useEffect(() => {
    // Uncomment if we need to use query params to set the auth state
    const authParam = queryParams.get('auth')
    if (authParam) {
      // Uncomment if we need to use query params to set the auth state
      // setAuthState(authParam as AuthFlowSteps)
    }
    return () => {
      setUserEmail('')
    }
    // eslint-disable-next-line
  }, [])

  // TODO: Make it work
  useEffect(() => {
    if (props.eventStage && props.user?.id && props.eventStage !== EventStages.REGISTRATION) {
      return history.push('/event')
    }
    // eslint-disable-next-line
  }, [props.eventStage, props.user])

  const onClose = () => {
    setAuthState(AuthFlowSteps.Root)
  }

  return (
    <div className={classes.root}>
      <Header preventFade={true} />
      <div className={classNames(props.backgroundImage && classes.backgroundImage)} />
      {authState !== AuthFlowSteps.Welcome && (
        <div className={classes.authWrapper}>
          <Grid container className={classes.authStateWrapper}>
            <Grid item xs={12}>
              <AuthRoot setAuthState={setAuthState} />
              {![AuthFlowSteps.Welcome, AuthFlowSteps.Root].includes(authState) && (
                <Modal
                  onClose={onClose}
                  closeFn={onClose}
                  open={true}
                  classes={{
                    container: classes.modalContainer
                  }}
                  disableBackdropClick
                >
                  <Box className={classes.container}>
                    <Paper className={classes.paper} square elevation={0}>
                      {authState === AuthFlowSteps.SignIn && (
                        <SignIn
                          setAuthState={setAuthState}
                          setUserEmail={setUserEmail}
                          redirectRoute='/event'
                          setUser={props.setUser}
                          eventStage={props.eventStage}
                        />
                      )}
                      {authState === AuthFlowSteps.SignUp && (
                        <SignUp setAuthState={setAuthState} setUserEmail={setUserEmail} />
                      )}
                      {authState === AuthFlowSteps.ConfirmSignUp && (
                        <ConfirmSignUp userEmail={userEmail} setAuthState={setAuthState} />
                      )}
                      {authState === AuthFlowSteps.ResendCode && (
                        <ResendCode userEmail={userEmail} setAuthState={setAuthState} />
                      )}
                      {authState === AuthFlowSteps.Register && (
                        <Registration
                          userEmail={userEmail}
                          setAuthState={setAuthState}
                          setUserEmail={setUserEmail}
                          setUser={props.setUser}
                          eventStage={props.eventStage}
                        />
                      )}
                      {authState === AuthFlowSteps.HaveAQuestion && (
                        <HaveAQuestion setAuthState={setAuthState} userEmail={userEmail} />
                      )}
                      {authState === AuthFlowSteps.Survey && (
                        <Survey
                          setAuthState={setAuthState}
                          userEmail={userEmail}
                          setUser={props.setUser}
                          eventStage={props.eventStage}
                        />
                      )}
                      {authState === AuthFlowSteps.ThankYou && <ThankYou />}
                      {authState === AuthFlowSteps.ForgotPassword && <ForgotPassword setAuthState={setAuthState} />}
                    </Paper>
                  </Box>
                </Modal>
              )}
            </Grid>
          </Grid>
        </div>
      )}
      <Footer />
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    minHeight: '100%',
    color: '#000',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      backgroundColor: '#fff',
      justifyContent: 'initial'
    }
  },
  backgroundImage: (props: IAuthWrapper) =>
    props.backgroundImage
      ? {
          backgroundImage: `url(${props.backgroundImage})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          [theme.breakpoints.up('md')]: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 0,
            position: 'fixed'
          },
          [theme.breakpoints.down('sm')]: {
            height: '210px',
            width: '100%',
            position: 'relative'
          }
        }
      : {},
  authWrapper: {
    zIndex: 1,
    backgroundColor: 'transparent',
    marginTop: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    minHeight: 700,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: '#fff',
      alignItems: 'flex-start',
      padding: '90px 2rem',
      minHeight: 200
    }
  },
  authStateWrapper: {
    zIndex: 1,
    [theme.breakpoints.up('md')]: {
      padding: '0 5%',
      marginTop: '2rem'
    }
  },
  modalContainer: {
    minWidth: '640px !important',
    width: '640px !important',
    padding: '5rem !important',
    [theme.breakpoints.down('sm')]: {
      width: '100vw !important',
      minWidth: 'initial !important',
      padding: '0 !important'
    }
  },
  paper: {
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: '2rem'
    }
  },
  container: {
    width: '100%'
  },
  footer: {
    backgroundColor: '#F6F6F6'
  }
}))
